import React, { useState } from 'react';
import { uploadExcel } from '../api/client';
import {
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface AdminUploadFormProps {
  onUploadSuccess?: () => void;
}

const AdminUploadForm: React.FC<AdminUploadFormProps> = ({ onUploadSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && !selectedFile.name.endsWith('.xlsx')) {
      setError('Please select an Excel (.xlsx) file');
      setFile(null);
      return;
    }
    setFile(selectedFile || null);
    setError(null);
    setSuccess(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', file);

      // const response = await uploadExcel(formData);
      await uploadExcel(formData);
      setSuccess('File uploaded successfully! The SKU mapping has been updated.');
      setFile(null);
      
      // Reset the file input
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Notify parent component of successful upload
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Box sx={{ my: 3 }}>
        <input
          accept=".xlsx"
          style={{ display: 'none' }}
          id="excel-file-upload"
          type="file"
          onChange={handleFileChange}
          disabled={uploading}
        />
        <label htmlFor="excel-file-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
            size="large"
            fullWidth
            sx={{ mb: 2 }}
          >
            Select Excel File (.xlsx)
          </Button>
        </label>
        
        {file && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
            <Typography variant="body2" className="text-blue-800">
              📄 Selected file: <strong>{file.name}</strong>
            </Typography>
            <Typography variant="caption" className="text-blue-600">
              Size: {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </div>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={!file || uploading}
        size="large"
        fullWidth
        sx={{ mt: 2 }}
      >
        {uploading ? (
          <>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            Uploading...
          </>
        ) : (
          'Upload & Replace SKU File'
        )}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        Note: The previous file will be automatically backed up before replacement.
      </Typography>
    </Paper>
  );
};

export default AdminUploadForm; 